// Day-14 Offer card — shown after Confidence Preview completion.
// 40% off Learner for 3 months ($9.99 → $5.99), redeemable within 7 days.
//
// Source: CogniPrint_Recalibration_Cadence_Research_v2.md §2.6,
// CogniPrint_Recalibration_Dev_Plan_v1.md §3.4 (REC-020).

import { useEffect, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@shared/services/firebase';
import type { Day14Offer } from '@shared/types';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';

interface Props {
  headline: string;
  dimensionDisplay: string;
  offer: Day14Offer;
  alreadyCompleted: boolean;
  onDismiss: () => void;
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function OfferCard({
  headline,
  dimensionDisplay,
  offer,
  alreadyCompleted,
  onDismiss,
}: Props) {
  const [copied, setCopied] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const daysLeft = daysUntil(offer.offerExpiresAt);
  const expired = daysLeft <= 0 || !!offer.redeemedAt;

  useEffect(() => {
    trackEvent({
      name: ANALYTICS_EVENTS.RECAL_OFFER_CARD_VIEWED,
      properties: { ...getBaseProperties(), cohort: offer.cohort },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(offer.promoCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Could not copy — select the code manually.');
    }
  }

  async function onUpgrade() {
    setCheckingOut(true);
    setError(null);
    try {
      trackEvent({
        name: ANALYTICS_EVENTS.RECAL_OFFER_CLICKED,
        properties: {
          ...getBaseProperties(),
          cohort: offer.cohort,
          promoCode: offer.promoCode,
        },
      });
      const fn = httpsCallable<
        { tier: string; promoCode: string },
        { checkoutUrl: string }
      >(getFunctions(app), 'createCheckout');
      const result = await fn({ tier: 'learner', promoCode: offer.promoCode });
      window.location.href = result.data.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setCheckingOut(false);
    }
  }

  return (
    <div>
      {!alreadyCompleted && (
        <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
          Here’s what we noticed
        </div>
      )}

      {!alreadyCompleted && headline && (
        <p
          className="font-heading font-semibold text-[16px] leading-snug text-white mb-5"
          style={{ letterSpacing: '-0.01em' }}
        >
          {headline}
        </p>
      )}

      {alreadyCompleted && (
        <>
          <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
            Your offer is still waiting
          </div>
          <p className="text-content-secondary text-sm mb-5">
            You already completed your Confidence Preview
            {dimensionDisplay ? ` (${dimensionDisplay})` : ''}. Your offer
            hasn’t been redeemed yet.
          </p>
        </>
      )}

      <div
        className="rounded-xl p-4 mb-4"
        style={{
          background:
            'linear-gradient(135deg, rgba(108,92,231,0.10), rgba(240,165,0,0.06))',
          border: '1px solid rgba(108,92,231,0.30)',
        }}
      >
        <div className="flex items-baseline justify-between mb-1">
          <div className="font-heading font-bold text-white text-[15px]">
            Unlock the full Confidence Check
          </div>
          <div className="text-[11px] font-semibold text-gold-500">
            {offer.discountPercent}% off
          </div>
        </div>
        <div className="text-content-muted text-[13px] mb-3 leading-relaxed">
          3 months of Learner at{' '}
          <span className="text-white font-semibold">$5.99/mo</span>{' '}
          <span className="text-content-dim">(normally $9.99)</span>
        </div>

        <div
          className="flex items-center justify-between rounded-lg"
          style={{
            padding: '10px 12px',
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div>
            <div className="text-[10px] uppercase tracking-wider text-content-dim mb-0.5">
              Promo code
            </div>
            <div className="font-code text-sm text-white">
              {offer.promoCode}
            </div>
          </div>
          <button
            onClick={onCopy}
            className="w-8 h-8 flex items-center justify-center rounded-md text-content-muted hover:text-content transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)' }}
            aria-label="Copy promo code"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-[11px] text-content-dim mb-4">
        <span>
          {expired
            ? offer.redeemedAt
              ? 'Offer redeemed'
              : 'Offer expired'
            : `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
        </span>
        <span>One-time offer</span>
      </div>

      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

      <div className="flex gap-2">
        <button
          onClick={onDismiss}
          className="flex-1 rounded-lg text-sm font-medium py-2.5 text-content-muted"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          Maybe later
        </button>
        <button
          onClick={onUpgrade}
          disabled={expired || checkingOut}
          className="flex-1 rounded-lg text-sm font-semibold py-2.5 text-white disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)' }}
        >
          {checkingOut ? 'Redirecting…' : 'Redeem offer'}
        </button>
      </div>
    </div>
  );
}
